import React from 'react';

import StyledComponent from '~/components/Styled/Styled';
import Matchup from '~/components/Matchup/Matchup';
import GameDetailsCard from '~/components/GameDetailsCard/GameDetailsCard';
import PlayerLineup from '~/components/PlayerLineup/PlayerLineup';
import TeamStatsGetTheApp from '~/components/TeamStatsGetTheApp/TeamStatsGetTheApp';
import TeamStats from '~/components/TeamStats/TeamStats';
import Timeline from '~/components/Timeline/Timeline';
import KeyPlayersCard from '~/components/KeyPlayersCard/KeyPlayersCard';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import SoccerPreGame from '~/components/SoccerPreGame/SoccerPreGame';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';

import getOr from '~/util/getOr';
import getPlayerLineup from '~/util/getPlayerLineup';
import getActionGoals from '~/util/getActionGoals';
import getActionShootout from '~/util/getActionShootout';
import getTimeline from '~/util/getTimeline';
import formatKeyPlayers from '~/util/formatKeyPlayers';

import normalise from '~/schemas/teamStats/normalise';
import { SOCCER_PREGAME } from '~/schemas/teamStats/soccerSchema';

import { injectIntl } from 'react-intl';
import css from './SoccerMatchup.scss';

type Props = {
  event: any;
  stats: any;
  intl: any;
};

type State = {
  loading: boolean;
  goals: any[];
  shots: any[];
  substitutions: any[];
  cards: any[];
  lineup: any[];
  timeline: any[];
  shootout: any[];
};

class SoccerMatchup extends React.Component<Props, State> {
  static displayName: string;

  static availbleForData(eventStatus) {
    return eventStatus === 'pre_game' || eventStatus === 'in_progress' || eventStatus === 'final';
  }

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      goals: [],
      shots: [],
      substitutions: [],
      cards: [],
      lineup: [],
      timeline: [],
      shootout: [],
    };
  }

  componentDidMount() {
    const eventStatus = this.props.event.event_status;

    this.setState({ loading: true });

    if (SoccerMatchup.availbleForData(eventStatus)) {
      this.getGoalsAndShootoutData()
        .then(() => {
          this.setState({ loading: false });
        })
        .catch(() => {
          this.setState({ loading: false });
        });
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(oldProps) {
    if (
      new Date(oldProps.event.updated_at).getTime() / 1000 !==
      new Date(this.props.event.updated_at).getTime() / 1000
    ) {
      this.getGoalsAndShootoutData();
    }
  }

  async getGoalsAndShootoutData() {
    const { event } = this.props;

    const leagueSlug = getOr(event, 'league.slug', '');
    const boxScoreId = getOr(event, 'box_score.id', '');
    const eventId = getOr(event, 'id', '');

    const dataToDownload = [];

    const timeline = await getTimeline(leagueSlug, eventId);
    const lineup = await getPlayerLineup(leagueSlug, eventId);

    dataToDownload.push(timeline);
    dataToDownload.push(lineup);

    let goals;
    let shootout;

    if (boxScoreId) {
      shootout = await getActionShootout(leagueSlug, boxScoreId);
      goals = await getActionGoals(leagueSlug, boxScoreId);

      dataToDownload.push(shootout);
      dataToDownload.push(goals);
    }

    const dataToDownloadValues = await Promise.allSettled([...dataToDownload]);

    const timelineRes = getOr(dataToDownloadValues[0], 'value', []);
    const lineupRes = getOr(dataToDownloadValues[1], 'value', []);

    const goalsDataToUpdate = {
      timeline: timelineRes,
      lineup: lineupRes,
      shootout: null,
    };

    if (dataToDownloadValues.length >= 3) {
      const shootoutRes = getOr(dataToDownloadValues[2], 'value', []);

      goalsDataToUpdate.shootout = shootoutRes;
    }

    this.setState(goalsDataToUpdate);
  }

  renderMatchup() {
    return (
      <MatchupSection>
        <Matchup event={this.props.event} />
      </MatchupSection>
    );
  }

  renderGameDetails() {
    return (
      <MatchupSection>
        <GameDetailsCard disableHeaderDetails eventDetails={this.props.event.event_details} />
      </MatchupSection>
    );
  }

  renderPlayerLineup() {
    if (!this.state.lineup.length) {
      return null;
    }

    return (
      <MatchupSection>
        <PlayerLineup lineup={this.state.lineup} />
      </MatchupSection>
    );
  }

  renderTeamStats() {
    const { event, stats, intl } = this.props;
    const homeTeamName = getOr(event, 'home_team.abbreviation', '');
    const awayTeamName = getOr(event, 'away_team.abbreviation', '');
    const statsToDisplay = normalise(stats.team, SOCCER_PREGAME);

    return (
      <MatchupSection>
        <div className={css.teamStatsCard}>
          <div className={css.cardHeader}>Team Comparison</div>
          <div className={css.teamStats}>
            <TeamStats
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              stats={statsToDisplay}
            />
          </div>
          {intl.locale === 'en' ? (
            <div className={css.teamStatsGetTheApp}>
              <TeamStatsGetTheApp leagueSlug="soccer" />
            </div>
          ) : null}
        </div>
      </MatchupSection>
    );
  }
  renderTimeline() {
    if (!this.state.timeline.length) {
      return null;
    }
    return (
      <MatchupSection>
        <Timeline event={this.props.event} timeline={this.state.timeline} />
      </MatchupSection>
    );
  }
  renderShootouts() {
    if (!this.state.shootout.length) {
      return null;
    }
    return (
      <MatchupSection>
        <Timeline event={this.props.event} shootouts={this.state.shootout} />
      </MatchupSection>
    );
  }
  renderLineup() {
    return (
      <MatchupSection>
        <PlayerLineup lineup={this.state.lineup} />
      </MatchupSection>
    );
  }
  renderKeyPlayersCard() {
    const title = this.props.event.event_status === 'pre_game' ? 'Key Players' : 'Top Performers';
    const keyPlayers = formatKeyPlayers(this.props.event);
    return (
      <MatchupSection>
        <KeyPlayersCard
          stats={keyPlayers.stats}
          players={keyPlayers.players}
          title={title}
          sport={this.props.event.sport_name}
        />
      </MatchupSection>
    );
  }

  render() {
    const isPreGame = this.props.event.event_status === 'pre_game';
    const inProgress = this.props.event.event_status === 'in_progress';
    const standings = getOr(this.props.event, 'standings', {});
    const awayLastResults = getOr(this.props.event, 'standings.away.last_results', '');
    const homeLastResults = getOr(this.props.event, 'standings.home.last_results', '');
    const teamForm =
      (Object.keys(standings).length > 0 && awayLastResults && homeLastResults) || '';
    const leagueSlug = getOr(this.props.event, 'league.slug', '');

    return (
      <div className="row">
        <div className={`${css.column} col-sm-8`}>
          {this.renderMatchup()}

          {this.state.loading ? <LoadingSpinner /> : null}

          {!this.state.loading ? this.renderTimeline() : null}
          <div className={css.mobileView}>{!inProgress && this.renderKeyPlayersCard()}</div>
          {!isPreGame && !this.state.loading ? this.renderShootouts() : null}
          {isPreGame && !this.state.loading ? this.renderTeamStats() : null}
          {!this.state.loading ? this.renderPlayerLineup() : null}
          <div className={css.mobileView}>{this.renderGameDetails()}</div>
        </div>
        <div className={`${css.column} ${css.desktopView} col-sm-4`}>
          {this.renderGameDetails()}
          <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
          {!inProgress && this.renderKeyPlayersCard()}
          {isPreGame ? (
            <SoccerPreGame
              event={this.props.event}
              isLoading={this.state.loading}
              teamForm={teamForm}
            />
          ) : null}
        </div>
      </div>
    );
  }
}

SoccerMatchup.displayName = 'WorldcupMatchup';

export default injectIntl(StyledComponent(SoccerMatchup, [css]));
